import React, { useState, useEffect } from 'react';

const FREE_LIMIT = 25;
const YEARLY_AMOUNT = 20;

export default function Upgrade({ userId }) {
  const [yearlyPriceId, setYearlyPriceId] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);

  const params = new URLSearchParams(window.location.search);
  const success = params.get('success') === '1';
  const cancelled = params.get('cancelled') === '1';

  useEffect(() => {
    async function load() {
      try {
        if (userId && success) {
          await fetch(`/api/stripe/sync-plan/${userId}`, { method: 'POST' });
        }
        const fetches = [fetch('/api/stripe/plans').then(r => r.json())];
        if (userId) fetches.push(fetch(`/api/stripe/subscription/${userId}`).then(r => r.json()));
        const [planData, subData] = await Promise.all(fetches);
        const yearly = planData?.prices?.find(p => p.interval === 'year');
        if (yearly) setYearlyPriceId(yearly.id);
        if (subData) setSubscription(subData);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [userId, success]);

  const startCheckout = async () => {
    if (!userId || !yearlyPriceId) return;
    setCheckoutLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, priceId: yearlyPriceId })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error || 'Could not start checkout');
    } catch (e) {
      setError(e.message);
      setCheckoutLoading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/stripe/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error || 'Could not open billing portal');
    } catch (e) {
      setError(e.message);
      setPortalLoading(false);
    }
  };

  const isPremium = subscription?.isPremium;

  return (
    <div style={{ maxWidth: '620px', margin: '0 auto', padding: '2.5rem 1.25rem', fontFamily: 'inherit' }}>
      {success && (
        <div style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '1.75rem', color: '#2E7D32', fontWeight: '500', fontSize: '0.95rem' }}>
          ✓ You're now on String Premium — enjoy unlimited contacts.
        </div>
      )}
      {cancelled && (
        <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '1.75rem', color: '#F57F17', fontSize: '0.95rem' }}>
          No problem — you can upgrade anytime.
        </div>
      )}

      {isPremium ? (
        <div>
          <h1 style={{ fontSize: '1.6rem', color: '#5D4037', margin: '0 0 0.4rem' }}>You're on Premium</h1>
          <p style={{ color: '#888', fontSize: '0.95rem', margin: '0 0 2rem', lineHeight: 1.6 }}>
            Thank you for supporting String. You have unlimited contacts.
          </p>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '1.75rem 2rem', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', border: '2px solid #5D4037', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <div style={{ fontWeight: '700', color: '#5D4037', fontSize: '1rem' }}>String Premium</div>
                <div style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.2rem' }}>Unlimited contacts · $20 / year</div>
              </div>
              <button
                onClick={openPortal}
                disabled={portalLoading || !userId}
                style={{ padding: '0.6rem 1.25rem', background: '#5D4037', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.88rem', fontWeight: '600', cursor: (portalLoading || !userId) ? 'default' : 'pointer', opacity: (portalLoading || !userId) ? 0.7 : 1, whiteSpace: 'nowrap' }}
              >
                {portalLoading ? 'Opening...' : 'Manage billing'}
              </button>
            </div>
          </div>
          <p style={{ color: '#ccc', fontSize: '0.78rem', textAlign: 'center' }}>
            To cancel or update your payment method, use the billing portal above.
          </p>
        </div>
      ) : (
        <div>
          <h1 style={{ fontSize: '1.6rem', color: '#5D4037', margin: '0 0 0.4rem' }}>Upgrade to Premium</h1>
          <p style={{ color: '#888', fontSize: '0.95rem', margin: '0 0 2rem', lineHeight: 1.6 }}>
            The free plan supports up to {FREE_LIMIT} contacts. Get unlimited for ${YEARLY_AMOUNT}/year.
          </p>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
            <div style={{ background: '#faf8f5', borderRadius: '14px', padding: '1.5rem 1.75rem', flex: 1, minWidth: '200px', border: '1px solid #e8e0d8' }}>
              <div style={{ fontSize: '0.72rem', color: '#bbb', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.6rem' }}>Free</div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#aaa', lineHeight: 1, marginBottom: '1.2rem' }}>$0</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.85rem', color: '#999', lineHeight: 2.1 }}>
                <li>Up to {FREE_LIMIT} contacts</li>
                <li>SMS + email reminders</li>
                <li>Birthday & anniversary alerts</li>
                <li>Holiday reminders</li>
              </ul>
              <div style={{ marginTop: '1.5rem', padding: '0.55rem', background: '#ede8e4', borderRadius: '7px', textAlign: 'center', color: '#bbb', fontSize: '0.82rem', fontWeight: '500' }}>
                Current plan
              </div>
            </div>

            <div style={{ background: '#fff', borderRadius: '14px', padding: '1.5rem 1.75rem', flex: 1, minWidth: '200px', border: '2px solid #5D4037', boxShadow: '0 4px 16px rgba(93,64,55,0.13)', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, right: 0, background: '#5D4037', color: '#fff', fontSize: '0.68rem', fontWeight: '700', padding: '4px 12px', borderBottomLeftRadius: '8px', letterSpacing: '0.06em' }}>
                PREMIUM
              </div>
              <div style={{ fontSize: '0.72rem', color: '#5D4037', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.6rem' }}>Premium</div>
              <div style={{ lineHeight: 1, marginBottom: '0.35rem' }}>
                <span style={{ fontSize: '2rem', fontWeight: '700', color: '#5D4037' }}>${YEARLY_AMOUNT}</span>
                <span style={{ color: '#aaa', fontSize: '0.85rem' }}> / year</span>
              </div>
              <div style={{ fontSize: '0.78rem', color: '#bbb', marginBottom: '1.2rem' }}>billed annually · cancel anytime</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.85rem', color: '#555', lineHeight: 2.1 }}>
                <li><strong>Unlimited contacts</strong></li>
                <li>SMS + email reminders</li>
                <li>Birthday & anniversary alerts</li>
                <li>Holiday reminders</li>
              </ul>
              <button
                onClick={startCheckout}
                disabled={checkoutLoading || loading || !yearlyPriceId || !userId}
                style={{ marginTop: '1.5rem', width: '100%', padding: '0.75rem', background: '#5D4037', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '600', cursor: (checkoutLoading || loading || !yearlyPriceId || !userId) ? 'default' : 'pointer', opacity: (checkoutLoading || loading || !yearlyPriceId || !userId) ? 0.7 : 1 }}
              >
                {checkoutLoading ? 'Redirecting to checkout...' : loading ? 'Loading...' : !userId ? 'Log in to upgrade' : `Get Premium — $${YEARLY_AMOUNT}/year`}
              </button>
            </div>
          </div>

          {error && (
            <div style={{ background: '#FFEBEE', color: '#C62828', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <p style={{ color: '#ccc', fontSize: '0.78rem', textAlign: 'center', margin: 0 }}>
            Payments processed securely by Stripe. Cancel anytime — no questions asked.
          </p>
        </div>
      )}
    </div>
  );
}
